import { useEffect, useRef } from "react";
import { requestBlackFridayPricing } from "@/checkout/lib/black-friday-pricing-request";
import { isBlackFridayPricingPaused } from "@/checkout/lib/black-friday-pricing-activity";

export function blackFridayPricingFingerprint(
	checkout:
		| {
				email?: string | null;
				voucherCode?: string | null;
				lines: readonly { id: string; quantity: number }[];
		  }
		| undefined
		| null,
): string {
	return JSON.stringify([
		checkout?.email,
		checkout?.voucherCode,
		checkout?.lines.map(({ id, quantity }) => [id, quantity]),
	]);
}
/** Scalar snapshot dependencies avoid repeated requests from fresh urql objects. */
export function useBlackFridayPricingSync(
	checkoutId: string | undefined | null,
	fingerprint: string,
	refresh: () => void,
	options: { syncOnMount?: boolean } = {},
) {
	const previous = useRef({ id: checkoutId, fingerprint });
	const refreshRef = useRef(refresh);
	useEffect(() => {
		refreshRef.current = refresh;
	}, [refresh]);
	const syncOnMount = options.syncOnMount ?? true;
	useEffect(() => {
		if (!checkoutId) return;
		if (!syncOnMount && previous.current.id === checkoutId && previous.current.fingerprint === fingerprint)
			return;
		previous.current = { id: checkoutId, fingerprint };
		let active = true;
		const timer = setTimeout(() => {
			if (isBlackFridayPricingPaused(checkoutId)) return;
			void requestBlackFridayPricing(checkoutId).then((status) => {
				if (active && status === "updated") refreshRef.current();
			});
		}, 250);
		return () => {
			active = false;
			clearTimeout(timer);
		};
	}, [checkoutId, fingerprint, syncOnMount]);
}
